import { useState } from 'react';
import { api, ApiError, type Pad, type PadSettings, type User } from '@/lib/api';
import type { PadPrefs } from '../PadScreen';
import {
  Alert, Modal, Toggle, copyText, useToast,
} from '../kit';
import { IconCopy, IconLock, IconCheck } from '../icons';

/**
 * Everything about this sketchpad and how you personally like to draw on it —
 * including the join code, for the host who did not write it down.
 */
export default function PadSettingsModal({
  pad, user, isHost, seats, prefs, onPrefsChange, onPadChange, onClose, onLeave,
}: {
  pad: Pad;
  user: User;
  isHost: boolean;
  /** Seats in this pad, from the host's plan. */
  seats: number;
  prefs: PadPrefs;
  onPrefsChange: (patch: Partial<PadPrefs>) => void;
  onPadChange: (pad: Pad) => void;
  onClose: () => void;
  onLeave: () => void;
}) {
  const [tab, setTab] = useState<'pad' | 'drawing' | 'people'>('pad');
  const [title, setTitle] = useState(pad.title);
  const [password, setPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  async function save(fields: Parameters<typeof api.updatePad>[1]) {
    setBusy(true);
    setError('');
    try {
      const res = await api.updatePad(pad.id, fields);
      onPadChange(res.pad);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const setPadSetting = (patch: Partial<PadSettings>) => save({ settings: patch });

  const copyCode = async () => {
    if (!pad.joinCode) return;
    const ok = await copyText(pad.joinCode);
    setCopied(ok);
    toast(ok ? 'Join code copied.' : 'Could not copy — select it manually.', ok ? 'default' : 'error');
    window.setTimeout(() => setCopied(false), 2000);
  };

  const rotateCode = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Get a new join code? The old one will stop working immediately.')) return;
    setBusy(true);
    try {
      const res = await api.rotateCode(pad.id);
      onPadChange(res.pad);
      toast('New join code created.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Sketchpad settings"
      onClose={onClose}
      wide
      footer={(
        <>
          <button type="button" className="btn btn--ghost" onClick={onLeave}>Leave sketchpad</button>
          <button type="button" className="btn" onClick={onClose}>Done</button>
        </>
      )}
    >
      <div className="tabs" style={{ marginBottom: 18 }}>
        <button type="button" className="tab" data-active={tab === 'pad'} onClick={() => setTab('pad')}>This pad</button>
        <button type="button" className="tab" data-active={tab === 'drawing'} onClick={() => setTab('drawing')}>Drawing</button>
        <button type="button" className="tab" data-active={tab === 'people'} onClick={() => setTab('people')}>People</button>
      </div>

      {error ? <Alert tone="error">{error}</Alert> : null}

      {tab === 'pad' ? (
        <>
          {pad.joinCode ? (
            <>
              <p className="popover__title">Join code</p>
              <div className="codebox">
                <span className="codebox__code selectable">{pad.joinCode}</span>
                <div className="row" style={{ gap: 6 }}>
                  <button type="button" className="pill-btn pill-btn--sm" onClick={copyCode} aria-label="Copy join code">
                    {copied ? <IconCheck size={20} /> : <IconCopy size={20} />}
                  </button>
                </div>
              </div>
              <p className="tiny muted" style={{ marginTop: 8 }}>
                Share this with up to {seats - 1}{seats - 1 === 1 ? ' friend' : ' friends'}. It lives here permanently, so you never
                have to remember it.
              </p>
              {isHost ? (
                <button type="button" className="btn btn--sm btn--ghost" onClick={rotateCode} disabled={busy} style={{ marginTop: 8 }}>
                  Get a new code
                </button>
              ) : null}
              <div className="divider" />
            </>
          ) : (
            <Alert tone="info">
              {pad.ownerName} hosts this sketchpad, so only they can see and share the join code.
            </Alert>
          )}

          {isHost ? (
            <>
              <div className="field">
                <label htmlFor="ps-title">Name</label>
                <div className="row">
                  <input
                    id="ps-title"
                    className="input grow"
                    value={title}
                    maxLength={60}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || title.trim() === pad.title || !title.trim()}
                    onClick={() => save({ title: title.trim() }).then((ok) => ok && toast('Renamed.'))}
                  >
                    Save
                  </button>
                </div>
              </div>

              <div className="setting">
                <div className="setting__text">
                  <div className="setting__label">
                    <IconLock size={14} /> Password
                  </div>
                  <div className="setting__hint">
                    {pad.hasPassword
                      ? 'Set — people need it as well as the code.'
                      : 'Not set — anyone with the code can join.'}
                  </div>
                </div>
                <button type="button" className="btn btn--sm btn--ghost" onClick={() => setChangingPassword((v) => !v)}>
                  {changingPassword ? 'Cancel' : 'Change'}
                </button>
              </div>

              {changingPassword ? (
                <div className="row" style={{ marginBottom: 14 }}>
                  <input
                    className="input grow"
                    type="text"
                    placeholder="Leave blank to remove the password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn btn--accent"
                    disabled={busy}
                    onClick={() => save({ password: password || null }).then((ok) => {
                      if (!ok) return;
                      toast(password ? 'Password set.' : 'Password removed.');
                      setPassword('');
                      setChangingPassword(false);
                    })}
                  >
                    Save
                  </button>
                </div>
              ) : null}

              <Toggle
                label="Lock the sketchpad"
                hint="Nobody new can join, even with the code. People already here stay."
                on={pad.settings.locked}
                onChange={(on) => setPadSetting({ locked: on })}
              />
              <Toggle
                label="Allow watchers"
                hint={`Once ${seats} people are drawing, extras can still watch without drawing.`}
                on={pad.settings.allowSpectators}
                onChange={(on) => setPadSetting({ allowSpectators: on })}
              />
              <Toggle
                label="Anyone can clear everything"
                hint="Off means only you can wipe the whole sketchpad."
                on={pad.settings.allowGuestClearAll}
                onChange={(on) => setPadSetting({ allowGuestClearAll: on })}
              />
              <Toggle
                label="Chat"
                on={pad.settings.chatEnabled}
                onChange={(on) => setPadSetting({ chatEnabled: on })}
              />
              <Toggle
                label="Laser pointer"
                hint="A temporary glow for pointing things out without drawing."
                on={pad.settings.laserEnabled}
                onChange={(on) => setPadSetting({ laserEnabled: on })}
              />
            </>
          ) : (
            <p className="muted tiny">
              Only the host can rename this sketchpad or change who may join.
            </p>
          )}
        </>
      ) : null}

      {tab === 'drawing' ? (
        <>
          <p className="popover__title">Paper</p>
          <div className="row row--wrap" style={{ gap: 8, marginBottom: 16 }}>
            {(['plain', 'dots', 'grid', 'lines'] as const).map((style) => (
              <button
                key={style}
                type="button"
                className="btn btn--sm btn--ghost"
                data-active={prefs.background === style}
                style={prefs.background === style
                  ? { background: 'var(--surface-active)', textTransform: 'capitalize' }
                  : { textTransform: 'capitalize' }}
                onClick={() => onPrefsChange({ background: style })}
              >
                {style}
              </button>
            ))}
          </div>

          <Toggle
            label="Dark paper"
            hint="Just for you — everyone else keeps their own choice."
            on={prefs.theme === 'dark'}
            onChange={(on) => onPrefsChange({ theme: on ? 'dark' : 'light' })}
          />
          <Toggle
            label="Pressure sensitivity"
            hint="Stylus and supported touchscreens draw thicker when you press harder."
            on={prefs.pressure}
            onChange={(on) => onPrefsChange({ pressure: on })}
          />
          <Toggle
            label="Smooth my lines"
            hint="Softens the wobble in a quick stroke."
            on={prefs.smoothing}
            onChange={(on) => onPrefsChange({ smoothing: on })}
          />
          <Toggle
            label="Hold to make perfect shapes"
            hint="Pause at the end of a line, box or circle and it straightens itself. Keep moving to carry on freehand."
            on={prefs.snapShapes}
            onChange={(on) => onPrefsChange({ snapShapes: on })}
          />
          <Toggle
            label="Scroll wheel zooms"
            hint="Off means the wheel pans and pinch or ⌘-scroll zooms."
            on={prefs.wheelZooms}
            onChange={(on) => onPrefsChange({ wheelZooms: on })}
          />
          <Toggle
            label="Show how far I can pan"
            hint="A dashed frame appears as you approach the edge of the drawable area."
            on={prefs.showPanFrontier}
            onChange={(on) => onPrefsChange({ showPanFrontier: on })}
          />
          <Toggle
            label="Left-handed layout"
            hint="Moves the tools to the other side of the screen."
            on={prefs.leftHanded}
            onChange={(on) => onPrefsChange({ leftHanded: on })}
          />
          <Toggle
            label="Ask before clearing"
            on={prefs.confirmClear}
            onChange={(on) => onPrefsChange({ confirmClear: on })}
          />
          <Toggle
            label="Show frame rate"
            hint="For when something feels slow."
            on={prefs.showFps}
            onChange={(on) => onPrefsChange({ showFps: on })}
          />
        </>
      ) : null}

      {tab === 'people' ? (
        <>
          <Toggle
            label="Show other people's cursors"
            on={prefs.showCursors}
            onChange={(on) => onPrefsChange({ showCursors: on })}
          />
          <Toggle
            label="Show their names on the cursor"
            on={prefs.showCursorNames}
            onChange={(on) => onPrefsChange({ showCursorNames: on })}
            disabled={!prefs.showCursors}
          />
          <Toggle
            label="Always outline where everyone is"
            hint="Keeps each person's screen framed on the canvas, not only in the overview."
            on={prefs.showPeerViewports}
            onChange={(on) => onPrefsChange({ showPeerViewports: on })}
          />
          <Toggle
            label="Autosave a preview"
            hint="Keeps the picture on your sketchpad list up to date."
            on={prefs.autosaveThumbnails}
            onChange={(on) => onPrefsChange({ autosaveThumbnails: on })}
          />
          <div className="divider" />
          <div className="tiny muted">
            Signed in as <strong>{user.username}</strong>. Your colour is how others see your cursor
            and the frame around your screen.
          </div>
        </>
      ) : null}
    </Modal>
  );
}
